import { ibkApi, selectAppDomain, selectSelectedCompanyDocument, selectSelectedCompanyOrgId, } from '@ibk/shared-config';
import { isMigrationAddressComplete } from '../utils/benefitMigrationAddress';
import { isBenefitsCompanyDocument, normalizeBenefitsDocument } from '../utils/benefitMigrationDocument';
import { selectBenefitMigrationUserId } from '../utils/benefitMigrationSession';
export function sameMigrationScope(left, right) {
    return (left.revision === right.revision &&
        left.generation === right.generation &&
        left.userId === right.userId &&
        left.identity.appDomain === right.identity.appDomain &&
        left.identity.organizationId === right.identity.organizationId &&
        left.identity.document === right.identity.document &&
        left.identity.legacyContractId === right.identity.legacyContractId);
}
const stepIds = {
    MIGRATION_STARTED: 'started',
    MIGRATING_BENEFICIARIES: 'collaborators',
    REQUESTING_CARDS: 'cards',
    MIGRATION_COMPLETED: 'completed',
};
const wireSteps = Object.keys(stepIds);
const states = { COMPLETED: 'completed', IN_PROGRESS: 'current', PENDING: 'pending' };
function trackingPhase(status, known, currentStep) {
    if (status === 'ACTIVATED')
        return 'complete';
    if (!known || !currentStep)
        return 'unavailable';
    if (currentStep === 'REQUESTING_CARDS')
        return 'cards-pending';
    return 'provisioning';
}
function trackingStepState(status, known, stepStatus) {
    if (status === 'ACTIVATED')
        return 'completed';
    if (known)
        return states[stepStatus];
    return 'pending';
}
export function emptyMigrationSnapshot(identity) {
    return {
        identity,
        phase: 'unavailable',
        emissorContractId: null,
        acceptedAt: null,
        addressConfirmedAt: null,
        cutoverAt: null,
        selfServiceEndsAt: null,
        historyAccess: 'unavailable',
        steps: wireSteps.map(step => ({ id: stepIds[step], state: 'pending', completedAt: null })),
    };
}
export function normalizeMigrationTracking(identity, contract, tracking) {
    if (tracking.contractId !== contract.id || tracking.legacyContractId !== identity.legacyContractId)
        throw new Error('IDENTITY_MISMATCH');
    if (!Array.isArray(tracking.steps) ||
        tracking.steps.length !== 4 ||
        wireSteps.some(step => tracking.steps.filter(item => item.step === step).length !== 1))
        throw new Error('INVALID_RESPONSE');
    if (tracking.currentStep !== null && !wireSteps.includes(tracking.currentStep))
        throw new Error('INVALID_RESPONSE');
    const known = ['REQUESTED', 'CREATED', 'ACTIVATED'].includes(tracking.contractStatus ?? '');
    const snapshot = emptyMigrationSnapshot(identity);
    snapshot.emissorContractId = contract.id;
    snapshot.phase = trackingPhase(tracking.contractStatus ?? '', known, tracking.currentStep);
    snapshot.steps = wireSteps.map(step => {
        const item = tracking.steps.find(value => value.step === step);
        if (!['COMPLETED', 'IN_PROGRESS', 'PENDING'].includes(item.status) ||
            (item.completedAt !== null &&
                (typeof item.completedAt !== 'string' || !Number.isFinite(Date.parse(item.completedAt)))))
            throw new Error('INVALID_RESPONSE');
        return {
            id: stepIds[step],
            state: trackingStepState(tracking.contractStatus ?? '', known, item.status),
            completedAt: item.completedAt,
        };
    });
    return {
        snapshot,
        terms: null,
        address: contract.address
            ? { ...contract.address, complement: contract.address.complement ?? undefined, recipientName: '' }
            : null,
        emissorContractLegalName: contract.legalName,
    };
}
function validContractSearchPage(items, document) {
    return (Array.isArray(items) &&
        items.every(item => item &&
            typeof item.id === 'string' &&
            Boolean(item.id) &&
            typeof item.document === 'string' &&
            normalizeBenefitsDocument(item.document) === document));
}
function isIncompleteSearchPage(itemsCount, contractCount, previousCount, page) {
    return itemsCount === 0 || contractCount === previousCount || page === 99;
}
function contractSearchTotal(response) {
    const totalHeader = response.meta?.response?.headers.get('X-Total-Count');
    const total = totalHeader === null || totalHeader === undefined ? null : Number(totalHeader);
    if (total !== null && (!Number.isInteger(total) || total < 0))
        return { valid: false };
    return { valid: true, total };
}
function isContractSearchComplete(total, offset, itemsCount, limit) {
    if (total !== null)
        return offset >= total;
    return itemsCount < limit;
}
async function scanMigrationContracts(document, transport) {
    const contracts = new Map();
    let offset = 0;
    const limit = 100;
    for (let page = 0; page < 100; page++) {
        const response = await transport({
            url: '/v1/benefits/contracts/search',
            method: 'POST',
            body: { document, limit, offset, sort: '-createdAt' },
        });
        if (response.error)
            return { kind: 'failure', result: response };
        const items = response.data?.result;
        if (!validContractSearchPage(items, document))
            return { kind: 'failure', result: { error: { code: 'IDENTITY_MISMATCH' } } };
        const before = contracts.size;
        items.forEach(item => contracts.set(item.id, item));
        if (contracts.size - before !== items.length)
            return { kind: 'failure', result: { error: { code: 'INCOMPLETE_CONTRACT_SEARCH' } } };
        const totalResult = contractSearchTotal(response);
        if (!totalResult.valid)
            return { kind: 'failure', result: { error: { code: 'INVALID_RESPONSE' } } };
        offset += items.length;
        const searchComplete = isContractSearchComplete(totalResult.total, offset, items.length, limit);
        if (searchComplete)
            break;
        if (isIncompleteSearchPage(items.length, contracts.size, before, page))
            return { kind: 'failure', result: { error: { code: 'INCOMPLETE_CONTRACT_SEARCH' } } };
    }
    return { kind: 'success', contracts };
}
async function readMigrationDashboard(contractId, transport) {
    try {
        return await transport({
            url: `/v1/benefits/contracts/${encodeURIComponent(contractId)}/dashboard`,
            method: 'GET',
        });
    }
    catch {
        return { error: { code: 'DASHBOARD_UNAVAILABLE' } };
    }
}
export async function discoverBenefitMigration(params, transport) {
    const document = normalizeBenefitsDocument(params.document);
    if (!isBenefitsCompanyDocument(document))
        return { error: { code: 'INVALID_DOCUMENT' } };
    const scan = await scanMigrationContracts(document, transport);
    if (scan.kind === 'failure')
        return scan.result;
    const { contracts } = scan;
    const matches = [...contracts.values()].filter(contract => contract.legacyContractId && (!params.legacyContractId || contract.legacyContractId === params.legacyContractId));
    if (matches.some(contract => typeof contract.legacyContractId !== 'string' || typeof contract.legalName !== 'string'))
        return { error: { code: 'INVALID_RESPONSE' } };
    if (matches.length > 1)
        return { error: { code: 'AMBIGUOUS_MIGRATION' } };
    if (!matches.length)
        return { data: { read: null } };
    const contract = matches[0];
    const identity = {
        appDomain: params.appDomain,
        organizationId: params.organizationId,
        document,
        legacyContractId: contract.legacyContractId,
    };
    const response = await transport({
        url: `/v1/benefits/contracts/${encodeURIComponent(contract.id)}/migration-tracking`,
        method: 'GET',
    });
    if (response.error)
        return response;
    try {
        const tracking = response.data?.result;
        if (!tracking)
            return { error: { code: 'INVALID_RESPONSE' } };
        const read = normalizeMigrationTracking(identity, contract, tracking);
        if (read.snapshot.phase === 'complete') {
            const dashboard = await readMigrationDashboard(contract.id, transport);
            if (dashboard.error?.code === 'STALE_MIGRATION_SCOPE')
                return dashboard;
            const shouldShowLegacyVersion = dashboard.data?.result
                ?.shouldShowLegacyVersion;
            if (!dashboard.error && typeof shouldShowLegacyVersion === 'boolean') {
                read.snapshot.historyAccess = shouldShowLegacyVersion ? 'available' : 'expired';
            }
        }
        return {
            data: { read },
        };
    }
    catch (error) {
        return { error: { code: error instanceof Error ? error.message : 'INVALID_RESPONSE' } };
    }
}
export const benefitMigrationApi = ibkApi.injectEndpoints({
    endpoints: build => ({
        discoverBenefitMigration: build.query({
            queryFn: async (params, _api, _extra, baseQuery) => {
                const isCurrent = () => {
                    const state = _api.getState();
                    return (!_api.signal.aborted &&
                        selectBenefitMigrationUserId(state) === params.userId &&
                        selectAppDomain(state) === params.appDomain &&
                        selectSelectedCompanyOrgId(state) === params.organizationId &&
                        normalizeBenefitsDocument(selectSelectedCompanyDocument(state) ?? '') ===
                            normalizeBenefitsDocument(params.document));
                };
                const stale = { error: { code: 'STALE_MIGRATION_SCOPE' } };
                const result = await discoverBenefitMigration(params, async (request) => {
                    if (!isCurrent())
                        return stale;
                    const response = (await baseQuery(request));
                    return isCurrent() ? response : stale;
                });
                if (result.error)
                    return { error: result.error };
                return { data: result.data };
            },
        }),
    }),
    overrideExisting: false,
});
export function migrationServiceError(scope, error) {
    const value = error;
    const code = value?.code ?? value?.data?.code ?? String(value?.status ?? 'NETWORK_ERROR');
    let reason = 'network';
    if (code === 'IDENTITY_MISMATCH' || code === 'AMBIGUOUS_MIGRATION')
        reason = 'identity-mismatch';
    else if (code === 'TIMEOUT_ERROR')
        reason = 'timeout';
    else if (value?.status === 409 ||
        [
            'ACTIVE_CONTRACT_EXISTS_FOR_DOCUMENT',
            'TERM_ACCEPTANCE_CONFLICT',
            'LEGACY_CONTRACT_ALREADY_LINKED',
            'LEGACY_CONTRACT_ALREADY_MIGRATED',
        ].includes(code))
        reason = 'conflict';
    else if (code === 'INVALID_ADDRESS')
        reason = 'invalid-address';
    else if (['INVALID_RESPONSE', 'INCOMPLETE_CONTRACT_SEARCH', 'INVALID_DOCUMENT'].includes(code))
        reason = 'configuration';
    return { kind: 'error', scope, reason, code };
}
export function buildMigrationCreateBody(input) {
    const document = normalizeBenefitsDocument(input.identity.document);
    if (!input.identity.legacyContractId || !isBenefitsCompanyDocument(document))
        throw new Error('IDENTITY_MISMATCH');
    if (!input.accepted)
        throw new Error('INVALID_CONSENT');
    if (!isMigrationAddressComplete(input.address))
        throw new Error('INVALID_ADDRESS');
    const { street, number, neighborhood, city, state, complement, postalCode } = input.address;
    return {
        document,
        legacyContractId: input.identity.legacyContractId,
        address: {
            street,
            number,
            neighborhood,
            postalCode: postalCode.replace(/\D/g, ''),
            city,
            state,
            complement: complement ?? null,
        },
    };
}
export function normalizeRequestedMigration(identity, contract) {
    if (!contract ||
        typeof contract.id !== 'string' ||
        !contract.id ||
        typeof contract.document !== 'string' ||
        normalizeBenefitsDocument(contract.document) !== normalizeBenefitsDocument(identity.document) ||
        contract.legacyContractId !== identity.legacyContractId)
        throw new Error('IDENTITY_MISMATCH');
    const snapshot = emptyMigrationSnapshot(identity);
    snapshot.emissorContractId = contract.id;
    snapshot.phase = 'provisioning';
    snapshot.steps = snapshot.steps.map(step => ({ ...step, state: step.id === 'started' ? 'current' : 'pending' }));
    return {
        snapshot,
        terms: null,
        address: contract.address
            ? { ...contract.address, complement: contract.address.complement ?? undefined, recipientName: '' }
            : null,
        emissorContractLegalName: contract.legalName,
    };
}
export function createBenefitMigrationService(read, isCurrent, creation) {
    return {
        async read(scope) {
            try {
                const value = await read(scope);
                if (!isCurrent(scope))
                    return { kind: 'error', scope, reason: 'stale' };
                return value
                    ? { kind: 'success', scope, value }
                    : { kind: 'error', scope, reason: 'unavailable', code: 'MIGRATION_NOT_FOUND' };
            }
            catch (error) {
                return migrationServiceError(scope, error);
            }
        },
        async submit(scope, input) {
            if (!isCurrent(scope))
                return { kind: 'error', scope, reason: 'stale' };
            if (!sameMigrationScope(scope, { ...scope, identity: input.identity }))
                return { kind: 'error', scope, reason: 'identity-mismatch' };
            try {
                const contract = await creation.create(buildMigrationCreateBody(input));
                if (!isCurrent(scope))
                    return { kind: 'error', scope, reason: 'stale' };
                const requested = normalizeRequestedMigration(scope.identity, contract);
                creation.onCreated(requested);
                return { kind: 'success', scope, value: requested.snapshot };
            }
            catch (error) {
                if (!isCurrent(scope))
                    return { kind: 'error', scope, reason: 'stale' };
                return migrationServiceError(scope, error instanceof Error ? { code: error.message } : error);
            }
        },
    };
}
